import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Gamepad2,
  Home,
  Lightbulb,
  Plus,
  Share2,
  Sparkles,
  Target,
  UserCircle,
  UserRound,
  Users,
  Video,
  X,
} from 'lucide-react';
import { listKidProfilesForParentSession, MAX_PARENT_KIDS, PARENT_KIDS_CHANGED_EVENT, type ParentKidProfileRow } from '../../../lib/parentKidProfiles';

interface LeftNavProps {
  isOpen: boolean;
  onClose?: () => void;
  onContentChange?: (content: string) => void;
  /** When true, desktop sidebar is a narrow icon rail (no expand strip). */
  collapsible?: boolean;
}

type NavItem = {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  disabled?: boolean;
};

const FUN_ITEMS: NavItem[] = [
  {
    name: 'Games',
    href: '/games',
    icon: Gamepad2,
    description: 'Play and practice',
  },
  {
    name: 'Social stories',
    href: '/social-stories',
    icon: BookOpen,
    description: 'Everyday situations',
  },
  {
    name: 'Comprehension',
    href: '/comprehension',
    icon: Sparkles,
    description: 'Reading game',
  },
  {
    name: 'Helpful videos',
    href: '/kid-profile?fun=videos',
    icon: Video,
    description: 'Quick parent watch list',
  },
  {
    name: 'Blog',
    href: '/community',
    icon: Users,
    description: 'Stories from parents',
  },
  {
    name: 'Tips and tricks',
    href: '/tips-and-tricks',
    icon: Lightbulb,
    description: 'Strategy bank',
  },
];

const FAMILY_ITEMS: NavItem[] = [
  {
    name: 'Important checklist',
    href: '/important-checklist',
    icon: ClipboardCheck,
    description: 'Starter safety and life goals',
  },
  {
    name: 'Focus areas',
    href: '/focus-areas',
    icon: Target,
    description: 'AI goals by developmental area',
  },
  {
    name: 'Assignments',
    href: '/assignments',
    icon: ClipboardList,
    description: 'Videos, screeners, goals, documents',
  },
  {
    name: 'Program referral',
    href: '/program-referral',
    icon: Share2,
    description: 'Share link and track signups',
  },
];

const ACCOUNT_ITEMS: NavItem[] = [
  {
    name: 'Profile settings',
    href: '/profile',
    icon: UserCircle,
    description: 'View and update your account',
  },
];

function kidDisplayName(name?: string | null): string {
  const raw = (name || '').trim().replace(/\s*\(.*\)\s*$/, '').trim();
  return raw || 'Child';
}

function itemIsActive(pathname: string, search: string, href?: string): boolean {
  if (!href) return false;
  const [itemPath, itemQuery = ''] = href.split('?');
  const itemParams = new URLSearchParams(itemQuery);
  const currentParams = new URLSearchParams(search);
  if (itemPath === '/kid-profile' && !itemParams.size) {
    return pathname === '/kid-profile' && !currentParams.get('fun');
  }
  const pathMatches =
    pathname === itemPath || (itemPath !== '/' && pathname.startsWith(`${itemPath}/`));
  const queryMatches =
    itemParams.size === 0 || [...itemParams.entries()].every(([k, v]) => currentParams.get(k) === v);
  if (itemParams.has('fun')) {
    return pathname === '/kid-profile' && currentParams.get('fun') === itemParams.get('fun');
  }
  return pathMatches && queryMatches;
}

const LeftNav: React.FC<LeftNavProps> = ({ isOpen, onClose, collapsible = false }) => {
  const location = useLocation();
  const [popup, setPopup] = useState<null | { title: string; body: string }>(null);
  const [kids, setKids] = useState<ParentKidProfileRow[]>([]);
  const [kidsOpen, setKidsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      listKidProfilesForParentSession()
        .then((rows) => {
          if (!cancelled) setKids(rows);
        })
        .catch(() => {
          if (!cancelled) setKids([]);
        });
    };
    load();
    window.addEventListener(PARENT_KIDS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(PARENT_KIDS_CHANGED_EVENT, load);
    };
  }, []);

  useEffect(() => {
    const onKid = kids.some((k) => location.pathname === `/kid-profile/${k.id}`);
    if (kids.length > 1 && onKid) setKidsOpen(true);
  }, [kids, location.pathname]);

  const isIconRail = collapsible;
  const navWidth = !isOpen
    ? 'w-0 overflow-hidden'
    : isIconRail
      ? 'lg:w-16 w-full sm:w-72'
      : 'w-full sm:w-72 lg:w-64';

  const sections = useMemo(
    () => [
      { label: 'Explore', items: FUN_ITEMS },
      { label: 'Goals', items: FAMILY_ITEMS },
      { label: 'Account', items: ACCOUNT_ITEMS },
    ],
    [],
  );

  const linkClass = (active: boolean, disabled?: boolean) =>
    `group flex items-center rounded-lg transition-colors font-sans w-full text-left ${
      isIconRail ? 'lg:justify-center lg:px-0 lg:py-2.5 gap-3 px-4 py-2.5' : 'gap-3 px-3 py-2.5'
    } ${
      disabled
        ? 'cursor-not-allowed text-white/35'
        : active
          ? 'bg-white/15 text-white'
          : 'text-white/75 hover:bg-white/10 hover:text-white'
    }`;

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = itemIsActive(location.pathname, location.search, item.href);
    const inner = (
      <>
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
          {item.name}
        </span>
      </>
    );

    return (
      <Link
        key={item.name}
        to={item.href!}
        onClick={() => onClose?.()}
        className={linkClass(active)}
        title={item.description}
        aria-label={item.name}
      >
        {inner}
      </Link>
    );
  };

  return (
    <div
      className={`h-full flex transition-all duration-300 ease-in-out overflow-hidden ${
        !isOpen && collapsible ? 'w-0' : ''
      } ${navWidth}`}
    >
      <nav
        className={`flex h-full min-w-0 flex-1 flex-col bg-black text-white ${
          !isOpen ? 'overflow-hidden' : ''
        }`}
      >
        <div className={`shrink-0 border-b border-white/10 ${isIconRail ? 'lg:px-2 px-4 py-4' : 'px-4 py-4'}`}>
          <p className={`text-sm font-semibold tracking-tight ${isIconRail ? 'lg:hidden' : ''}`}>
            TinyWins
          </p>
          {isIconRail ? (
            <p className="hidden text-center text-xs font-bold tracking-widest text-white/80 lg:block">
              TW
            </p>
          ) : null}
        </div>
        <div className="flex-1 overflow-y-auto py-3">
          <div className={`mb-4 ${isIconRail ? 'lg:px-1.5 px-3' : 'px-3'}`}>
            <div className="space-y-0.5">
              <Link
                to="/kid-profile"
                onClick={() => onClose?.()}
                className={linkClass(itemIsActive(location.pathname, location.search, '/kid-profile'))}
                title="Dashboard"
                aria-label="Home"
              >
                <Home className="h-5 w-5 flex-shrink-0" />
                <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                  Home
                </span>
              </Link>

              {kids.length >= MAX_PARENT_KIDS ? (
                <button
                  type="button"
                  className={linkClass(false, true)}
                  title={`You can have up to ${MAX_PARENT_KIDS} kids`}
                  aria-label="Add kid"
                  onClick={() =>
                    setPopup({
                      title: 'Kid limit',
                      body: `You can add up to ${MAX_PARENT_KIDS} kids. Delete a profile first if you need to add another.`,
                    })
                  }
                >
                  <Plus className="h-5 w-5 flex-shrink-0" />
                  <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                    Add kid
                  </span>
                </button>
              ) : (
                <Link
                  to="/kid-profile-form"
                  onClick={() => onClose?.()}
                  className={linkClass(location.pathname === '/kid-profile-form')}
                  title="Add a kid"
                  aria-label="Add kid"
                >
                  <Plus className="h-5 w-5 flex-shrink-0" />
                  <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                    Add kid
                  </span>
                </Link>
              )}

              {kids.length === 1 ? (
                <Link
                  to={`/kid-profile/${kids[0].id}`}
                  onClick={() => onClose?.()}
                  className={linkClass(location.pathname === `/kid-profile/${kids[0].id}`)}
                  title={kidDisplayName(kids[0].name)}
                  aria-label={kidDisplayName(kids[0].name)}
                >
                  <UserRound className="h-5 w-5 flex-shrink-0" />
                  <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                    {kidDisplayName(kids[0].name)}
                  </span>
                </Link>
              ) : null}

              {kids.length > 1 ? (
                <>
                  <button
                    type="button"
                    className={linkClass(kids.some((k) => location.pathname === `/kid-profile/${k.id}`))}
                    title="Kids"
                    aria-label="Kids"
                    aria-expanded={kidsOpen}
                    onClick={() => setKidsOpen((open) => !open)}
                  >
                    <UserRound className="h-5 w-5 flex-shrink-0" />
                    <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                      Kids
                    </span>
                    <ChevronDown
                      className={`ml-auto h-4 w-4 flex-shrink-0 transition-transform duration-300 ease-out ${
                        kidsOpen ? 'rotate-180' : ''
                      } ${isIconRail ? 'lg:hidden' : ''}`}
                    />
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      kidsOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div
                      className={`min-h-0 overflow-hidden ${isIconRail ? 'lg:pl-0 pl-4' : 'pl-4'}`}
                    >
                      <div
                        className={`space-y-0.5 pb-0.5 transition-opacity duration-200 ease-out ${
                          kidsOpen ? 'opacity-100 delay-75' : 'opacity-0'
                        }`}
                      >
                        {kids.map((kid) => {
                          const label = kidDisplayName(kid.name);
                          const href = `/kid-profile/${kid.id}`;
                          return (
                            <Link
                              key={kid.id}
                              to={href}
                              tabIndex={kidsOpen ? 0 : -1}
                              onClick={() => onClose?.()}
                              className={linkClass(location.pathname === href)}
                              title={label}
                              aria-label={label}
                            >
                              <span className="h-5 w-5 flex-shrink-0 text-center text-xs font-semibold leading-5 text-white/60">
                                ·
                              </span>
                              <span className={`truncate text-sm font-medium ${isIconRail ? 'lg:hidden' : ''}`}>
                                {label}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {sections.map((section) => (
            <div key={section.label} className={`mb-4 ${isIconRail ? 'lg:px-1.5 px-3' : 'px-3'}`}>
              <p
                className={`mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40 ${
                  isIconRail ? 'lg:hidden' : ''
                }`}
              >
                {section.label}
              </p>
              <div className="space-y-0.5">{section.items.map(renderItem)}</div>
            </div>
          ))}
        </div>
      </nav>

      {popup ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="leftnav-popup-title"
            className="w-full max-w-sm rounded-2xl bg-white p-5 text-left shadow-2xl"
          >
            <div className="flex items-start justify-between gap-3">
              <h2 id="leftnav-popup-title" className="text-lg font-semibold text-slate-900">
                {popup.title}
              </h2>
              <button
                type="button"
                onClick={() => setPopup(null)}
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-600">{popup.body}</p>
            <button
              type="button"
              onClick={() => {
                setPopup(null);
                onClose?.();
              }}
              className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LeftNav;
